import { useState, useEffect, useCallback, useRef } from 'react';

const WISDOM_FACTS = [
  // Eastern Wisdom
  {
    text: "The man who moves a mountain begins by carrying away small stones.",
    source: "Confucius",
    category: "wisdom",
  },
  {
    text: "In the middle of difficulty lies opportunity.",
    source: "Albert Einstein",
    category: "wisdom",
  },
  {
    text: "The journey of a thousand miles begins with a single step.",
    source: "Lao Tzu",
    category: "wisdom",
  },
  {
    text: "Fall seven times, stand up eight.",
    source: "Japanese proverb",
    category: "wisdom",
  },
  {
    text: "The obstacle is the path.",
    source: "Zen proverb",
    category: "wisdom",
  },
  {
    text: "Knowledge without practice is useless. Practice without knowledge is dangerous.",
    source: "Confucius",
    category: "wisdom",
  },
  {
    text: "Be not afraid of going slowly; be afraid only of standing still.",
    source: "Chinese proverb",
    category: "wisdom",
  },
  {
    text: "Vision without action is a daydream. Action without vision is a nightmare.",
    source: "Japanese proverb",
    category: "wisdom",
  },
  {
    text: "When you have eliminated the impossible, whatever remains, however improbable, must be the truth.",
    source: "Arthur Conan Doyle",
    category: "wisdom",
  },
  {
    text: "The best time to plant a tree was twenty years ago. The second best time is now.",
    source: "Chinese proverb",
    category: "wisdom",
  },
  {
    text: "To know and not to do is not yet to know.",
    source: "Zen saying",
    category: "wisdom",
  },

  // Sudoku & Puzzle Facts
  {
    text: "There are exactly 6,670,903,752,021,072,936,960 valid completed Sudoku grids — yet each puzzle you solve has only one unique solution.",
    source: "Sudoku mathematics",
    category: "math",
  },
  {
    text: "The minimum number of given clues for a Sudoku puzzle with a unique solution is 17. No valid 16-clue puzzle has ever been found.",
    source: "2012 proof by McGuire et al.",
    category: "math",
  },
  {
    text: "If you removed the symmetry constraints, Sudoku would have over 5.4 billion essentially different grids — a number larger than the world's population.",
    source: "Combinatorics",
    category: "math",
  },

  // Prime Numbers
  {
    text: "The largest known prime number has over 41 million digits. Written out in full, it would fill over 9,000 pages.",
    source: "2024 — 2^136,279,841 − 1",
    category: "math",
  },
  {
    text: "Prime numbers are called the 'atoms of mathematics' — every integer greater than 1 is either prime or can be built from primes in exactly one way.",
    source: "Fundamental Theorem of Arithmetic",
    category: "math",
  },
  {
    text: "Cicadas emerge in cycles of 13 or 17 years — both prime numbers — minimizing encounters with predators on shorter cycles. Evolution discovered primes before we did.",
    source: "Evolutionary biology",
    category: "math",
  },

  // Pi
  {
    text: "Pi has no known end, no discernible pattern, and never repeats. It contains every possible finite sequence of digits — your birthday, your phone number, and every book ever written, encoded within it.",
    source: "The mystery of π",
    category: "math",
  },
  {
    text: "Pi appears in the equation for the shape of rivers. The ratio of a river's actual meandering length to its straight-line distance averages approximately π.",
    source: "Hans-Henrik Stølum, 1996",
    category: "math",
  },
  {
    text: "A circle drawn with a diameter of 1 has a circumference of exactly π — an irrational number that connects the finite to the infinite.",
    source: "Euclidean geometry",
    category: "math",
  },

  // Mandelbrot, Buddhabrot & Mysticism
  {
    text: "The Mandelbrot set contains infinite complexity generated from the simplest equation: z² + c. Its boundary has infinite length, yet encloses a finite area.",
    source: "Fractal geometry",
    category: "fractal",
  },
  {
    text: "The Buddhabrot — a rendering of the Mandelbrot set's escaping orbits — produces an image strikingly resembling a seated Buddha. The mathematics existed billions of years before anyone saw it.",
    source: "Melinda Green, 1993",
    category: "fractal",
  },
  {
    text: "Ancient mystics depicted Ganesha, the Buddha, and the paisley motifs of Eastern tapestry long before fractals could be graphed. They saw with their minds what mathematics would later confirm with machines.",
    source: "The universality of pattern",
    category: "fractal",
  },
  {
    text: "Paisley — the teardrop pattern woven into Eastern textiles for millennia — mirrors the shapes found in fractal mathematics. The artisans intuited what equations would later describe.",
    source: "Art meets mathematics",
    category: "fractal",
  },
  {
    text: "Fractals appear in coastlines, lightning, blood vessels, ferns, and galaxies. The same recursive pattern repeats at every scale — as above, so below.",
    source: "Benoit Mandelbrot",
    category: "fractal",
  },
  {
    text: "The Mandelbrot set is often called the 'thumbprint of God' — a single formula producing infinite, non-repeating complexity that no human designed.",
    source: "Fractal philosophy",
    category: "fractal",
  },

  // Math as Universal Language
  {
    text: "Mathematics is not a human invention. It is the language in which the universe was written — discovered, not created, by those patient enough to listen.",
    source: "Mathematical philosophy",
    category: "philosophy",
  },
  {
    text: "Galileo wrote: 'The universe is written in the language of mathematics, and its characters are triangles, circles, and geometric figures, without which it is impossible to understand a single word.'",
    source: "Galileo Galilei, 1623",
    category: "philosophy",
  },
  {
    text: "Euler's identity — e^(iπ) + 1 = 0 — unites five fundamental constants in one equation. It has been called the most beautiful theorem in mathematics.",
    source: "Leonhard Euler, 1748",
    category: "philosophy",
  },
  {
    text: "The golden ratio φ appears in sunflower spirals, nautilus shells, DNA helices, and galaxy arms. Nature speaks in ratios we are only beginning to hear.",
    source: "The golden ratio 1.618…",
    category: "philosophy",
  },
  {
    text: "Every snowflake forms a six-fold symmetric crystal — not by design, but because the hydrogen bonds in water molecules naturally arrange at 120° angles. Beauty is a consequence of physics.",
    source: "Crystallography",
    category: "philosophy",
  },
  {
    text: "Music is math you can hear. The octave is a 2:1 frequency ratio, the perfect fifth is 3:2, the perfect fourth is 4:3. Harmony is arithmetic.",
    source: "Pythagoras",
    category: "philosophy",
  },
  {
    text: "0 times 0 equals infinity. The nothingness IS a something. That something became aware of itself, its full potential — to be established eternally — whose key is Life, Love, and Truth, made manifest in two words of one immensely powerful sentiment: 'I am.'",
    source: "The philosophy of zero",
    category: "philosophy",
  },
];

const ROTATE_INTERVAL = 90000; // 90 seconds
const FADE_DURATION = 600; // ms — matches CSS animation

export function useWisdomFact() {
  const usedIndices = useRef(new Set());
  
  const pickRandom = useCallback(() => {
    if (usedIndices.current.size >= WISDOM_FACTS.length) {
      usedIndices.current.clear();
    }
    let index;
    do {
      index = Math.floor(Math.random() * WISDOM_FACTS.length);
    } while (usedIndices.current.has(index));
    usedIndices.current.add(index);
    return WISDOM_FACTS[index];
  }, []);

  const [fact, setFact] = useState(() => pickRandom());
  const [fading, setFading] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setFact(pickRandom());
        setFading(false);
      }, FADE_DURATION);
    }, ROTATE_INTERVAL);

    return () => clearInterval(interval);
  }, [pickRandom]);

  return { fact, fading };
}

export { WISDOM_FACTS };
